import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { CustomButton } from "@/components/ui/Buttons";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  nextStep,
  updateField,
  updateV2FieldKeyPair,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import {
  BODY_TYPE_OPTIONS,
  GENDER_OPTIONS,
  SKIN_TONE_OPTIONS,
} from "@/constant/applicationFormConstant";
import FileUploadProfileDesign from "@/components/ui/FileUploadProfileDesign";
import AuthInput from "../../AuthInput";
import AuthDropdown from "../../AuthDropdown";
import AuthOptionGroup from "../../AuthOptionGroup";
import InputField from "@/components/form/InputField";
import { FileDetails } from "@/types/types";
import ProfilePhotoUploader from "./components/ProfilePhotoUploader";
import {
  scrollToFirstError,
  validateRequiredFields,
} from "@/utils/formValidationV2";

type ApplicationStep1Props = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const ApplicationStep1 = ({ setCurrentStep }: ApplicationStep1Props) => {
  const dispatch = useAppDispatch();

  const [errors, setErrors] = useState<Record<string, string>>({});

  // validation handler
  const handleSubmit = () => {
    if (profilePhoto && !profilePhoto.live_url) return;
    const formValues = {
      fullName,
      age,
      gender,
      bodyType,
      skinTone,
      // profilePhoto,
    };

    const validationErrors = validateRequiredFields(formValues, [
      "fullName",
      "age",
      "gender",
      "bodyType",
      "skinTone",
      // "profilePhoto",
    ]);

    if (age === null || !Number.isInteger(age) || age < 1 || age > 100) {
      validationErrors.age = "Enter a valid age between 1 and 100";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      scrollToFirstError(validationErrors);
      return;
    }

    setErrors({});
    dispatch(nextStep());
    setCurrentStep((prev) => prev + 1);
  };

  // Clear Function
  const clearFieldError = (field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  // OLD payload fields
  const fullName = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.fullName,
  );
  const age = useAppSelector((s) => s.signUpV2.SubmitApplicationInfo.age);
  const gender = useAppSelector((s) => s.signUpV2.SubmitApplicationInfo.gender);
  const bodyType = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.bodyType,
  );
  const skinTone = useAppSelector(
    (s) => s.signUpV2.SubmitApplicationInfo.skinTone,
  );

  const selectedGender = gender
    ? GENDER_OPTIONS.find((o) => o.key === gender) ?? null
    : null;

  const selectedBodyType = bodyType
    ? BODY_TYPE_OPTIONS.find((o) => o.key === bodyType) ?? null
    : null;

  const selectedSkinTone = skinTone
    ? SKIN_TONE_OPTIONS.find((o) => o.key === skinTone) ?? null
    : null;

  const profilePhoto = useAppSelector((s) => s.signUpV2.v2Extras.profilePhoto);
  const externalFiles: FileDetails[] = profilePhoto ? [profilePhoto] : [];
  return (
    <section>
      <div className="mb-8">
        <div className="flex items-center gap-x-2 whitespace-nowrap">
          <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
            <p className="text-white text-[11px] leading-5 tracking-normal">
              Step 1 of 6
            </p>
          </div>
          <h1 className="text-[#060B13] text-lg lg:text-xl tracking-[-0.7px] font-medium">
            Profile Basics
          </h1>
        </div>
        <p className="text-[#475467] text-xs tracking-[-0.5%] mt-2">
          Just the essentials to get your creator profile started.
        </p>
      </div>

      <div className="space-y-5">
        {/* <FileUploadProfileDesign
          useExternalState
          externalFiles={externalFiles}
          setExternalFiles={(next) => {
            // ✅ support both array and function updater
            const nextArr =
              typeof next === "function" ? next(externalFiles) : next;

            dispatch(
              updateV2FieldKeyPair({
                key: "profilePhoto",
                value: nextArr?.[0] ?? null,
              }),
            );
          }}
          onFilesUploaded={(files) => {
            dispatch(
              updateV2FieldKeyPair({
                key: "profilePhoto",
                value: files?.[0] ?? null,
              }),
            );
          }}
        /> */}
        <div id="profilePhoto">
          <ProfilePhotoUploader />
          {errors.profilePhoto && (
            <p className="text-red-500 text-xs mt-1">{errors.profilePhoto}</p>
          )}
        </div>
        {/* full name -> OLD payload */}
        <div id="fullName">
          <AuthInput
            label="Full Name"
            name="fullName"
            placeholder="Enter your full name"
            value={fullName}
            onChange={(e) => {
              dispatch(updateField("fullName", e.target.value));
              clearFieldError("fullName");
            }}
            error={errors.fullName}
          />
        </div>

        <div className="flex flex-col md:flex-row gap-y-5 items-center gap-x-[26px]">
          <div className="w-full">
            <div id="age">
              <AuthInput
                type="number"
                label="Age"
                name="age"
                min={1}
                max={100}
                step={1}
                placeholder="Enter your age"
                value={age ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === "" ||
                    (/^\d+$/.test(value) && Number(value) <= 100)
                  ) {
                    dispatch(
                      updateField("age", value === "" ? null : Number(value)),
                    );
                    clearFieldError("age");
                  } else {
                    setErrors((prev) => ({
                      ...prev,
                      age: "Enter a valid age between 1 and 100",
                    }));
                  }
                }}
                error={errors.age}
              />
            </div>
          </div>

          <div id="gender" className="w-full">
            <AuthDropdown
              label="Gender"
              options={GENDER_OPTIONS}
              value={selectedGender}
              onChange={(opt) => {
                dispatch(updateField("gender", String(opt.key)));
                clearFieldError("gender");
              }}
              error={errors.gender}
            />
          </div>
        </div>

        {/* body type -> OLD payload */}
        <div id="bodyType">
          <AuthOptionGroup
            label="Body Type"
            options={BODY_TYPE_OPTIONS}
            value={selectedBodyType}
            onChange={(opt) => {
              dispatch(updateField("bodyType", String(opt.key)));
              clearFieldError("bodyType");
            }}
          />
          {errors.bodyType && (
            <p className="text-red-500 text-xs mt-1">{errors.bodyType}</p>
          )}
        </div>

        {/* skin tone -> OLD payload */}
        <div id="skinTone">
          <AuthOptionGroup
            label="Skin Tone"
            options={SKIN_TONE_OPTIONS}
            value={selectedSkinTone}
            onChange={(opt) => {
              dispatch(updateField("skinTone", String(opt.key)));
              clearFieldError("skinTone");
            }}
          />
          {errors.skinTone && (
            <p className="text-red-500 text-xs mt-1">{errors.skinTone}</p>
          )}
        </div>
      </div>

      <div className="mt-[50px] text-end">
        <CustomButton
          backgroundColor="#FF6600"
          fontSize="14px"
          fontWeight="600"
          color="white"
          padding="5px 10.5px"
          borderRadius="200px"
          className="tracking-[-0.11px]"
          onClick={handleSubmit}
          disabled={!!profilePhoto && !profilePhoto.live_url}
        >
          Proceed
        </CustomButton>
      </div>
    </section>
  );
};

export default ApplicationStep1;
